/**
 * ProgressionLine - Handles the athlete progression line overlay
 */

import * as d3 from 'd3';
import { ProgressionPoint } from '../types/athlete.types';
import { ANIMATION_DURATION } from '../constants';

export class ProgressionLine {
    private g: any = null;
    private xScale: d3.ScaleLinear<number, number> | null = null;
    private barHeight: number = 20;
    private barPadding: number = 1;

    /**
     * Initializes the progression line group
     */
    init(parentG: any): void {
        this.g = parentG.append('g').attr('class', 'progression-line-group');
    }

    /**
     * Updates the progression line
     */
    update(
        progression: ProgressionPoint[],
        xScale: d3.ScaleLinear<number, number>,
        barHeight: number,
        barPadding: number,
        rankingMode: 'cumulative' | 'individual',
        currentStage: number
    ): void {
        if (!this.g) return;

        this.xScale = xScale;
        this.barHeight = barHeight;
        this.barPadding = barPadding;

        if (progression.length < 2) {
            this.hide();
            return;
        }

        const lineData = this.calculateLineData(progression, rankingMode, currentStage);
        this.renderLine(lineData);
        this.renderDots(lineData);
        this.renderLabels(lineData);
    }

    /**
     * Calculates line coordinates
     */
    private calculateLineData(
        progression: ProgressionPoint[],
        rankingMode: 'cumulative' | 'individual',
        currentStage: number
    ): Array<{x: number; y: number; stage: number; rank: number; time: number}> {
        if (!this.xScale) return [];

        return progression.map(d => {
            let x: number;
            if (rankingMode === 'individual') {
                const chartWidth = this.xScale!.range()[1];
                const stepWidth = chartWidth / (currentStage + 1);
                x = stepWidth * d.stage;
            } else {
                x = this.xScale!(d.time);
            }

            const y = (d.rank - 1) * (this.barHeight + this.barPadding) + (this.barHeight / 2);
            return { x, y, stage: d.stage, rank: d.rank, time: d.time };
        });
    }

    /**
     * Renders the line path
     */
    private renderLine(lineData: Array<{x: number; y: number}>): void {
        if (!this.g) return;

        const line = d3.line<{x: number; y: number}>()
            .x(d => d.x)
            .y(d => d.y)
            .curve(d3.curveLinear);

        let path = this.g.select('.progression-path');
        if (path.empty()) {
            path = this.g.append('path')
                .attr('class', 'progression-path')
                .style('fill', 'none')
                .style('stroke', 'white')
                .style('stroke-width', '3px')
                .style('opacity', 0.9);
        }

        path.datum(lineData)
            .transition()
            .duration(ANIMATION_DURATION)
            .ease(d3.easeQuadOut)
            .attr('d', line);
    }

    /**
     * Renders dots on the line
     */
    private renderDots(lineData: Array<{x: number; y: number; stage: number}>): void {
        if (!this.g) return;

        const dots = this.g.selectAll('.progression-dot')
            .data(lineData, (d: any) => d.stage);

        dots.exit()
            .transition()
            .duration(500)
            .attr('r', 0)
            .remove();

        const dotsEnter = dots.enter()
            .append('circle')
            .attr('class', 'progression-dot')
            .attr('cx', d => d.x)
            .attr('cy', d => d.y)
            .attr('r', 0)
            .style('fill', 'white')
            .style('stroke', 'var(--surface-color)')
            .style('stroke-width', '2px');

        dotsEnter.merge(dots as any)
            .transition()
            .duration(ANIMATION_DURATION)
            .ease(d3.easeQuadOut)
            .attr('cx', d => d.x)
            .attr('cy', d => d.y)
            .attr('r', 4);
    }

    /**
     * Renders rank labels
     */
    private renderLabels(lineData: Array<{x: number; y: number; stage: number; rank: number}>): void {
        if (!this.g) return;

        const labels = this.g.selectAll('.progression-label')
            .data(lineData, (d: any) => d.stage);

        labels.exit()
            .transition()
            .duration(500)
            .style('opacity', 0)
            .remove();

        const labelsEnter = labels.enter()
            .append('text')
            .attr('class', 'progression-label')
            .attr('x', d => d.x)
            .attr('y', d => d.y - 10)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('fill', 'white')
            .style('font-weight', 'bold')
            .style('opacity', 0)
            .text(d => `#${d.rank}`);

        labelsEnter.merge(labels as any)
            .transition()
            .duration(ANIMATION_DURATION)
            .ease(d3.easeQuadOut)
            .attr('x', d => d.x)
            .attr('y', d => d.y - 10)
            .style('opacity', 1)
            .text(d => `#${d.rank}`);
    }

    /**
     * Hides the progression line
     */
    hide(): void {
        this.g?.selectAll('*').remove();
    }

    /**
     * Brings the progression line to the front
     */
    raise(): void {
        this.g?.raise();
    }
}
